// ============================================================
// ELC Shift — Uploads / Checklists module (v104.0)
//
// Checklist templates (Cl* prefix in legacy code). Distinct
// from Uploads.logs — these are interactive ticked-off
// checklists vs free-text log entries.
//
// Public surface:
//   • renderClHistory
//   • loadClTemplates
//   • seedWeeklySafetyChecklistIfMissing
//   • renderClTemplateList
//   • renderClTemplateEditor
//   • saveClTemplate
//   • toggleClTemplateActive
//   • deleteClTemplate
//
// Free globals consumed:
//   DB, showToast, escapeHtml, formatDateShort, ALL_STORES
// ============================================================

export async function renderClHistory() {
  const storeFilter = document.getElementById('clHistStore')?.value  || '';
  const typeFilter  = document.getElementById('clHistType')?.value   || '';
  const dateFrom    = document.getElementById('clHistDateFrom')?.value || '';
  const dateTo      = document.getElementById('clHistDate')?.value   || '';

  const list = document.getElementById('clHistoryList');
  if (!list) return;
  list.innerHTML = '<div style="text-align:center;padding:30px;color:var(--sub)">Loading…</div>';

  // v123.x — server-backed (real rows + ids) so managers/admins can CORRECT a
  // submitted checklist. Replaces the old localStorage-only view (which only
  // showed THIS device's submissions and had no row id to edit).
  let rows;
  try {
    rows = await DB.checklists.list({ store: storeFilter || undefined, from: dateFrom || undefined, to: dateTo || undefined });
  } catch (e) {
    list.innerHTML = '<div style="text-align:center;padding:30px;color:var(--red)">Could not load checklists — try again.</div>';
    return;
  }
  // Map DB rows (answers live under items JSONB) → flat display objects, keeping
  // the row id (for corrections) and any audit fields.
  let subs = (Array.isArray(rows) ? rows : []).map(r => {
    const it = (r.items && typeof r.items === 'object') ? r.items : {};
    return {
      id: r.id, store: r.store || '', date: r.date || '', type: r.type || 'open',
      answers: it.answers || {}, score: it.score || 0, total: it.total || 1,
      submittedAt: it.submitted_at || '—', staffName: it.staff_name || 'Unknown',
      corrected: !!it.corrected, correctedBy: it.corrected_by || null,
    };
  });
  if (typeFilter) subs = subs.filter(s => s.type === typeFilter);
  subs.sort((a, b) => ((b.date || '') + (b.submittedAt || '')).localeCompare((a.date || '') + (a.submittedAt || '')));

  if (!subs.length) {
    list.innerHTML = '<div style="text-align:center;padding:40px;color:var(--sub)"><div style="font-size:36px;margin-bottom:10px">📋</div>No submissions found — try clearing filters or a wider date range.</div>';
    return;
  }

  list.innerHTML = `<div style="font-size:12px;color:var(--sub);margin-bottom:8px">${subs.length} submission${subs.length === 1 ? '' : 's'} found — click a row to expand, then Correct to fix a wrong tick.</div>`;
  subs.forEach((sub, idx) => {
    const total = sub.total || 1;
    const pct = Math.round(((sub.score || 0) / total) * 100);
    const scoreClass = pct === 100 ? 'perfect' : pct >= 80 ? 'warn' : 'fail';

    const card = document.createElement('div');
    card.className = 'cl-history-card';

    const hdr = document.createElement('div');
    hdr.className = 'cl-history-hdr';
    hdr.innerHTML = `
      <span class="cl-history-type ${sub.type === 'open' ? 'open' : 'close'}">${sub.type === 'open' ? '☀️ Opening' : '🌙 Closing'}</span>
      <div class="cl-history-meta">
        <div class="cl-history-store" translate="no">${escapeHtml((sub.store || '').replace('ELC - ', ''))}</div>
        <div class="cl-history-sub">${escapeHtml(sub.date || '—')} · ${escapeHtml(sub.submittedAt || '—')} · <span translate="no">${escapeHtml(sub.staffName || '—')}</span>${sub.corrected ? ' · ✏️ corrected by <span translate="no">' + escapeHtml(sub.correctedBy || 'manager') + '</span>' : ''}</div>
      </div>
      <div class="cl-history-score ${scoreClass}">${escapeHtml(sub.score || 0)}/${total} <span style="font-size:10px">(${pct}%)</span></div>
      <div class="cl-history-chevron" id="clChev${idx}">›</div>
    `;

    const body = document.createElement('div');
    body.className = 'cl-history-body';
    body.id = 'clBody' + idx;
    _clRenderBodyReadonly(body, sub);

    hdr.addEventListener('click', () => {
      const isOpen = body.classList.toggle('open');
      document.getElementById('clChev' + idx).classList.toggle('open', isOpen);
    });

    card.appendChild(hdr);
    card.appendChild(body);
    list.appendChild(card);
  });
}

// Read-only view of a submission's items + a "Correct" button (managers/admins).
function _clRenderBodyReadonly(body, sub) {
  body.innerHTML = '';
  const tmpl = adminGetTemplate(sub.type || 'open');
  const answers = sub.answers || {};
  const noItems = Object.values(answers).filter(a => a && a.answer === 'no');
  if (noItems.length) {
    const flag = document.createElement('div');
    flag.style.cssText = 'margin-bottom:10px;padding:8px 10px;background:var(--danger-bg);border-radius:8px;font-size:11px;color:var(--danger-deep);font-weight:600';
    flag.textContent = `⚠️ ${noItems.length} item${noItems.length > 1 ? 's' : ''} marked No`;
    body.appendChild(flag);
  }
  let globalIdx = 0;
  tmpl.forEach(section => {
    const secHdr = document.createElement('div');
    secHdr.style.cssText = 'font-size:10px;font-weight:800;color:var(--green);text-transform:uppercase;letter-spacing:.5px;padding:8px 0 4px';
    secHdr.textContent = section.section;
    secHdr.setAttribute('translate', 'no');   // v135.2: a Head Office section title, kept as written
    body.appendChild(secHdr);
    section.items.forEach(itemText => {
      const ans = answers[globalIdx];
      const row = document.createElement('div');
      row.className = 'cl-history-item';
      const ansIcon = !ans ? '–' : ans.answer === 'yes' ? '✅' : '❌';
      row.innerHTML = `<span class="cl-history-item-ans">${ansIcon}</span><div class="cl-history-item-text" translate="no">${escapeHtml(itemText)}${ans && ans.comment ? '<div class="cl-history-item-comment">💬 ' + escapeHtml(ans.comment) + '</div>' : ''}</div>`;
      body.appendChild(row);
      globalIdx++;
    });
  });
  const correctBtn = document.createElement('button');
  correctBtn.className = 'btn btn-outline btn-sm';
  correctBtn.style.cssText = 'margin-top:10px';
  correctBtn.textContent = '✏️ Correct';
  correctBtn.addEventListener('click', () => _clRenderBodyEdit(body, sub));
  body.appendChild(correctBtn);
}

// Editable view — Yes/No + comment per item, then Save (PATCH) or Cancel.
function _clRenderBodyEdit(body, sub) {
  body.innerHTML = '';
  const tmpl = adminGetTemplate(sub.type || 'open');
  const draft = JSON.parse(JSON.stringify(sub.answers || {}));   // edit a copy
  const note = document.createElement('input');
  note.type = 'text';
  note.placeholder = 'Reason for the correction (optional)';
  note.style.cssText = 'width:100%;box-sizing:border-box;border:1px solid var(--border);border-radius:8px;padding:8px;font-size:12px;font-family:inherit;margin-bottom:8px';

  const intro = document.createElement('div');
  intro.style.cssText = 'font-size:11px;color:var(--sub);margin-bottom:8px';
  intro.textContent = 'Correcting this checklist — every change is logged against your name.';
  body.appendChild(intro);
  body.appendChild(note);

  let globalIdx = 0;
  tmpl.forEach(section => {
    const secHdr = document.createElement('div');
    secHdr.style.cssText = 'font-size:10px;font-weight:800;color:var(--green);text-transform:uppercase;letter-spacing:.5px;padding:8px 0 4px';
    secHdr.textContent = section.section;
    secHdr.setAttribute('translate', 'no');   // v135.2: a Head Office section title, kept as written
    body.appendChild(secHdr);
    section.items.forEach(itemText => {
      const i = globalIdx++;
      const row = document.createElement('div');
      row.style.cssText = 'padding:6px 0;border-bottom:1px solid var(--border)';
      const txt = document.createElement('div');
      txt.style.cssText = 'font-size:12px;margin-bottom:4px';
      txt.textContent = itemText;
      txt.setAttribute('translate', 'no');   // v135.2: Head Office wrote it, so the Arabic layer leaves it as written
      row.appendChild(txt);
      const btns = document.createElement('div');
      btns.style.cssText = 'display:flex;gap:6px';
      const yes = document.createElement('button');
      const no = document.createElement('button');
      const cur = draft[i] && draft[i].answer;
      const styleBtn = (b, on, color) => { b.style.cssText = 'flex:1;padding:6px;border-radius:6px;border:1.5px solid ' + color + ';background:' + (on ? color : 'var(--card)') + ';color:' + (on ? '#fff' : color) + ';font-weight:700;font-size:12px;cursor:pointer;font-family:inherit'; };
      yes.textContent = '✓ Yes'; no.textContent = '✗ No';
      styleBtn(yes, cur === 'yes', '#16a34a');
      styleBtn(no, cur === 'no', '#dc2626');
      yes.addEventListener('click', () => { draft[i] = { answer: 'yes', comment: (draft[i] && draft[i].comment) || '' }; styleBtn(yes, true, '#16a34a'); styleBtn(no, false, '#dc2626'); });
      no.addEventListener('click', () => { draft[i] = { answer: 'no', comment: (draft[i] && draft[i].comment) || '' }; styleBtn(no, true, '#dc2626'); styleBtn(yes, false, '#16a34a'); });
      btns.appendChild(yes); btns.appendChild(no);
      row.appendChild(btns);
      body.appendChild(row);
    });
  });

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:8px;margin-top:10px';
  const save = document.createElement('button');
  save.className = 'btn btn-green btn-sm'; save.style.flex = '1'; save.textContent = '💾 Save correction';
  const cancel = document.createElement('button');
  cancel.className = 'btn btn-outline btn-sm'; cancel.textContent = 'Cancel';
  cancel.addEventListener('click', () => _clRenderBodyReadonly(body, sub));
  save.addEventListener('click', async () => {
    save.disabled = true; save.textContent = 'Saving…';
    try {
      const updated = await DB.checklists.correct(sub.id, { answers: draft, note: note.value || null });
      const it = (updated && updated.items && typeof updated.items === 'object') ? updated.items : {};
      sub.answers = it.answers || draft;
      sub.score = it.score != null ? it.score : sub.score;
      sub.corrected = true;
      if (typeof showToast === 'function') showToast('Checklist corrected — change logged.', 'success');
      _clRenderBodyReadonly(body, sub);
    } catch (e) {
      save.disabled = false; save.textContent = '💾 Save correction';
      if (typeof showToast === 'function') showToast('Correction failed: ' + ((e && e.message) || 'try again'), 'error');
    }
  });
  actions.appendChild(save); actions.appendChild(cancel);
  body.appendChild(actions);
}

export async function loadClTemplates() {
  try {
    const rows = await DB.checklistTemplates.list();
    clTemplates = Array.isArray(rows) ? rows : [];
  } catch(e) {
    console.warn('[DB] Template load failed:', e.message);
    clTemplates = [];
  }
  // v55: idempotent auto-seed of the weekly Safety & Compliance checklist.
  // Only the "admin" role creates it — area/store managers shouldn't trigger writes.
  // Runs at most one DB write ever (check-then-create pattern against in-memory list).
  try {
    if (currentAdmin && currentAdmin.role === 'admin') {
      await seedWeeklySafetyChecklistIfMissing();
    }
  } catch(e) { console.warn('[seed] weekly safety checklist:', e.message); }
  renderClTemplateList();
}

export async function seedWeeklySafetyChecklistIfMissing() {
  const TYPE = 'weekly_safety';
  if (clTemplates.find(t => t.type === TYPE)) return; // already seeded
  const payload = {
    type: TYPE,
    name: 'Weekly Safety & Compliance',
    frequency: 'weekly',
    frequency_config: { days: ['Mon','Sun'] },
    active: true,
    required: true,
    sort_order: (clTemplates.length || 0) + 10,
    items: [
      { section: '1 | Health & Safety', items: [
        'Floors clean, dry, and free from trip hazards',
        'Fire exits clearly marked and completely clear',
        'Fire extinguisher in place and in date',
        'First aid kit fully stocked and accessible',
        'All demo toys working safely with no sharp edges',
        'No broken or potentially dangerous toys on display',
        'CCTV system operational (check with mall management)',
        'Store alarm system tested per mall requirements',
        'Emergency contact numbers displayed and current',
        'Manual handling equipment in good condition',
        'Lights checked — all working',
        'Shelves checked — no broken or unstable fixtures',
        'Faulty goods processed (RFS procedure)',
        'Store perimeter vacuumed — including up high',
      ]},
      { section: '2 | Operational Standards', items: [
        'All staff wearing correct uniform and name badges',
        'Staff rotas issued and shared with Area Manager',
        'All required permits and licences current and displayed',
        'Economic Department compliance certificates up to date',
        'Staff Induction Books up to date for probationary staff',
      ]},
      { section: '3 | Stock Management', items: [
        'Stockroom organised to company standards',
        'Faulty and broken demo items processed, stickered and ready to be cleared on the next delivery',
        'Promotional stock properly displayed and signed',
        'Review of full store merchandising standards using merchandising standards document',
        'Top 10 stock check completed — submit results via the Top 10 Stock Check log',
      ]},
    ],
  };
  await DB.checklistTemplates.save(payload);
  clTemplates.push(payload);
  console.log('[seed] Created "Weekly Safety & Compliance" template (type=' + TYPE + ')');
}

export function renderClTemplateList() {
  const list = document.getElementById('clTemplateList');
  if (!list) return;
  list.innerHTML = '';

  if (!clTemplates.length) {
    list.innerHTML = '<div class="card" style="text-align:center;padding:24px;color:var(--sub)">No templates yet. Click "+ New Checklist" to create one.</div>';
    return;
  }

  // Sort by sort_order then name
  const sorted = clTemplates.slice().sort((a,b) => {
    const oa = a.sort_order || 0, ob = b.sort_order || 0;
    if (oa !== ob) return oa - ob;
    return (a.name || a.type || '').localeCompare(b.name || b.type || '');
  });

  sorted.forEach(t => {
    const items = Array.isArray(t.items) ? t.items : [];
    const itemCount = items.reduce((n, s) => n + (Array.isArray(s.items) ? s.items.length : 0), 0);
    const freqLabel = freqDescribe(t.frequency || 'daily', t.frequency_config || {});
    const active = t.active !== false;

    const card = document.createElement('div');
    card.className = 'card';
    card.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:8px;padding:14px;flex-wrap:wrap';
    card.innerHTML = `
      <div style="flex:1;min-width:200px">
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
          <strong style="font-size:14px" translate="no">${escapeHtml(t.name || t.type || 'Untitled')}</strong>
          ${active
            ? '<span style="font-size:10px;background:var(--ok-bg);color:var(--ok-deep);padding:2px 8px;border-radius:6px;font-weight:700">ACTIVE</span>'
            : '<span style="font-size:10px;background:var(--danger-bg);color:var(--danger-deep);padding:2px 8px;border-radius:6px;font-weight:700">DISABLED</span>'
          }
        </div>
        <div style="font-size:12px;color:var(--sub);margin-top:4px">
          ${freqLabel} · ${itemCount} item${itemCount === 1 ? '' : 's'}
        </div>
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap">
        <button class="btn btn-outline btn-sm" onclick="editClTemplate('${escapeAttr(t.type)}')">Edit</button>
        <button class="btn btn-outline btn-sm" onclick="Uploads.checklists.toggleClTemplateActive('${escapeAttr(t.type)}')">${active ? 'Disable' : 'Enable'}</button>
        <button class="btn btn-outline btn-sm" style="color:var(--danger);border-color:var(--adm-danger-line)" onclick="Uploads.checklists.deleteClTemplate('${escapeAttr(t.type)}')">Delete</button>
      </div>
    `;
    list.appendChild(card);
  });
}

export function renderClTemplateEditor() {
  if (!clTplEditing) return;
  const ed = document.getElementById('clTemplateEditor');
  if (!ed) return;   // v136.8: null-safe (the box may not be on screen)
  ed.innerHTML = '';
  const data = clTplEditing.items || [];
  data.forEach((section, si) => {
    const sDiv = document.createElement('div');
    sDiv.className = 'cl-tpl-section';

    const sHdr = document.createElement('div');
    sHdr.className = 'cl-tpl-section-hdr';
    const sName = document.createElement('input');
    sName.className = 'cl-tpl-section-name';
    sName.value = section.section || '';
    sName.addEventListener('input', () => { clTplEditing.items[si].section = sName.value; });
    const sDelBtn = document.createElement('button');
    sDelBtn.className = 'cl-tpl-del-section';
    sDelBtn.textContent = 'Remove section';
    sDelBtn.addEventListener('click', () => { clTplEditing.items.splice(si, 1); renderClTemplateEditor(); });
    sHdr.appendChild(sName);
    sHdr.appendChild(sDelBtn);
    sDiv.appendChild(sHdr);

    (section.items || []).forEach((item, ii) => {
      const row = document.createElement('div');
      row.className = 'cl-tpl-item-row';
      const inp = document.createElement('input');
      inp.className = 'cl-tpl-item-input';
      inp.value = item;
      inp.addEventListener('input', () => { clTplEditing.items[si].items[ii] = inp.value; });
      const del = document.createElement('button');
      del.className = 'cl-tpl-del-btn';
      del.textContent = '×';
      del.title = 'Remove item';
      del.addEventListener('click', () => { clTplEditing.items[si].items.splice(ii, 1); renderClTemplateEditor(); });
      row.appendChild(inp);
      row.appendChild(del);
      sDiv.appendChild(row);
    });

    const addItem = document.createElement('button');
    addItem.className = 'cl-tpl-add-item';
    addItem.textContent = '+ Add item';
    addItem.addEventListener('click', () => { clTplEditing.items[si].items.push(''); renderClTemplateEditor(); });
    sDiv.appendChild(addItem);
    ed.appendChild(sDiv);
  });
}

export async function saveClTemplate() {
  if (!clTplEditing) return;

  const name = document.getElementById('clTplName').value.trim();
  if (!name) { showToast('Please give the checklist a name', 'error'); return; }

  const frequency = document.getElementById('clTplFrequency').value;
  const active = document.getElementById('clTplActive').checked;
  // D1 — "required for compliance" (default true). Off = never flagged as missed.
  const clReqEl = document.getElementById('clTplRequired');
  const required = clReqEl ? clReqEl.checked : true;
  const cfg = {};
  if (frequency === 'weekly')  {
    // v55: collect checked days. Require at least one.
    const checked = Array.from(document.querySelectorAll('.cl-tpl-freq-day'))
      .filter(cb => cb.checked).map(cb => cb.value);
    if (!checked.length) { showToast('Weekly checklists need at least one day picked', 'error'); return; }
    cfg.days = checked;
  }
  if (frequency === 'monthly') cfg.date = parseInt(document.getElementById('clTplFreqDate').value, 10) || 1;

  // strip empty items
  const clean = (clTplEditing.items || []).map(s => ({
    section: (s.section || '').trim() || 'Untitled Section',
    items: (s.items || []).map(i => (i || '').trim()).filter(i => i.length > 0),
  })).filter(s => s.items.length > 0);

  if (!clean.length) { showToast('Add at least one item before saving', 'error'); return; }

  const payload = {
    type: clTplEditing.type,
    name,
    frequency,
    frequency_config: cfg,
    active,
    required,
    sort_order: clTplEditing.sort_order || 0,
    items: clean,
  };

  try {
    await DB.checklistTemplates.save(payload);
    showToast('"' + name + '" saved ✓', 'success');
    clTplEditing = null;
    document.getElementById('clTemplateEditorCard').style.display = 'none';
    await loadClTemplates();
  } catch(e) {
    console.error('[DB] Template save failed:', e);
    showToast('Save failed: ' + (e.message || 'unknown error'), 'error');
  }
}

export async function toggleClTemplateActive(type) {
  const t = clTemplates.find(x => x.type === type);
  if (!t) return;
  const newActive = !(t.active !== false); // flip
  try {
    await DB.checklistTemplates.update(type, { active: newActive });
    showToast(t.name + ' ' + (newActive ? 'enabled' : 'disabled'), 'success');
    await loadClTemplates();
  } catch(e) {
    showToast('Update failed: ' + (e.message || 'unknown'), 'error');
  }
}

export async function deleteClTemplate(type) {
  const t = clTemplates.find(x => x.type === type);
  if (!t) return;
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Delete "' + t.name + '"? This cannot be undone. Previous submissions of this checklist will remain in History.', { danger: true, confirmLabel: 'Delete' }))) return;
  try {
    await DB.checklistTemplates.remove(type);
    showToast(t.name + ' deleted', 'success');
    await loadClTemplates();
  } catch(e) {
    showToast('Delete failed: ' + (e.message || 'unknown'), 'error');
  }
}

